import React from 'react';
import { Box, Button, Checkbox, Flex, NumberInput, Select, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { Accordion } from 'react-bootstrap';
import { IconInfoCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { EventVisibility, GoogleEventResponseStatus, Transparency } from '../../utils/rule';
import { RemindersInput } from './RemindersInput';
import { SyncRuleFormValues } from './interface';
import { getSyncRuleColors } from './formDefaults';

const getStatusToHuman = (t: (key: string) => string) => ({
    [GoogleEventResponseStatus.needs_action]: t('dashboard.sync.status.options.unanswered'),
    [GoogleEventResponseStatus.declined]: t('dashboard.sync.status.options.declined'),
    [GoogleEventResponseStatus.tentative]: t('dashboard.sync.status.options.tentative'),
    [GoogleEventResponseStatus.accepted]: t('dashboard.sync.status.options.accepted'),
});

const getVisibilityToHuman = (t: (key: string) => string) => ({
    [EventVisibility.DEFAULT]: t('dashboard.sync.visibility.options.default'),
    [EventVisibility.PRIVATE]: t('dashboard.sync.visibility.options.private'),
    [EventVisibility.CONFIDENTIAL]: t('dashboard.sync.visibility.options.confidential'),
    [EventVisibility.PUBLIC]: t('dashboard.sync.visibility.options.public'),
    [EventVisibility.KEEP_ORIGINAL]: t('dashboard.sync.visibility.options.keep-original'),
});

const getVisibilityOptionTooltips = (t: (key: string) => string): Partial<Record<EventVisibility, string>> => ({
    [EventVisibility.KEEP_ORIGINAL]: t('dashboard.sync.visibility.option-tooltips.keep-original'),
    [EventVisibility.DEFAULT]: t('dashboard.sync.visibility.option-tooltips.default'),
});

const visibilityOptionOrder = [
    EventVisibility.KEEP_ORIGINAL,
    EventVisibility.DEFAULT,
    EventVisibility.PRIVATE,
    EventVisibility.CONFIDENTIAL,
    EventVisibility.PUBLIC,
];

const getTransparencyToHuman = (t: (key: string) => string) => ({
    [Transparency.keep_original]: t('dashboard.sync.transparency.options.keep-original'),
    [Transparency.ignore_transparent]: t('dashboard.sync.transparency.options.ignore-transparent'),
    [Transparency.ignore_opaque]: t('dashboard.sync.transparency.options.ignore-opaque'),
    [Transparency.set_to_transparent]: t('dashboard.sync.transparency.options.set-to-transparent'),
    [Transparency.set_to_opaque]: t('dashboard.sync.transparency.options.set-to-opaque'),
});

export const SyncRuleAdvancedSettings: React.FC<{
    form: UseFormReturnType<SyncRuleFormValues>;
    isDestinationColourCompatible: boolean;
    defaultOpenKey?: string;
}> = ({ form, isDestinationColourCompatible, defaultOpenKey = 'general' }) => {
    const { t } = useTranslation(['app']);
    const statusToHuman = getStatusToHuman(t);
    const visibilityToHuman = getVisibilityToHuman(t);
    const visibilityOptionTooltips = getVisibilityOptionTooltips(t);
    const transparencyToHuman = getTransparencyToHuman(t);
    const colors = getSyncRuleColors(t);

    const statusOptions = Object.values(GoogleEventResponseStatus).map(status => ({
        value: status,
        label: statusToHuman[status],
    }));

    const visibilityOptions = visibilityOptionOrder.map(visibility => ({
        value: visibility,
        label: visibilityToHuman[visibility],
    }));

    const transparencyOptions = Object.values(Transparency).map(transparency => ({
        value: transparency,
        label: transparencyToHuman[transparency],
    }));

    return (
        <Accordion defaultActiveKey={defaultOpenKey}>
            <Accordion.Item eventKey="general">
                <Accordion.Header>{t('dashboard.sync.general')}</Accordion.Header>
                <Accordion.Body>
                    <Stack gap="sm">
                        <Select
                            key="visibility"
                            label={
                                <Flex gap="xs" justify="flex-start" align="center" mb="0%">
                                    <Text size="sm" fw={500}>{t('dashboard.sync.visibility.label')}</Text>
                                    <Tooltip label={t('dashboard.sync.visibility.tooltip')} multiline w={400}>
                                        <IconInfoCircle size={16} />
                                    </Tooltip>
                                </Flex>
                            }
                            defaultValue={EventVisibility.KEEP_ORIGINAL}
                            data={visibilityOptions}
                            renderOption={({ option }) => {
                                const tooltip = visibilityOptionTooltips[option.value as EventVisibility];
                                const label = <Text size="sm">{option.label}</Text>;
                                if (!tooltip) {
                                    return label;
                                }
                                return (
                                    <Tooltip label={tooltip} multiline w={400}>
                                        {label}
                                    </Tooltip>
                                );
                            }}
                            allowDeselect={false}
                            {...form.getInputProps('visibility')}
                        />
                        <Select
                            disabled={!isDestinationColourCompatible}
                            w="fit-content"
                            label={t('dashboard.sync.label-color')}
                            data={colors.map(color => ({
                                value: color.id,
                                label: color.name,
                            }))}
                            renderOption={({ option }) => (
                                <Flex align="center" gap="sm" w="100%">
                                    <Box
                                        h={16}
                                        w={32}
                                        style={{
                                            backgroundColor: colors.find(c => c.id === option.value)?.background,
                                            borderRadius: '4px',
                                        }}
                                    />
                                    <Text size="sm">{option.label}</Text>
                                </Flex>
                            )}
                            allowDeselect={false}
                            {...form.getInputProps('color')}
                        />
                        <Select
                            maw="500px"
                            label={t('dashboard.sync.label-include-all-day-events')}
                            data={[
                                { value: '1', label: t('dashboard.sync.all-day-events.options.both') },
                                { value: '2', label: t('dashboard.sync.all-day-events.options.only-all-day') },
                                { value: '3', label: t('dashboard.sync.all-day-events.options.only-normal') },
                            ]}
                            allowDeselect={false}
                            {...form.getInputProps('includeAllDayEvents')}
                        />
                        <Checkbox
                            label={
                                <Flex gap="xs" align="center">
                                    <Text size="sm">{t('dashboard.sync.past-events.label')}</Text>
                                    <Tooltip label={t('dashboard.sync.past-events.tooltip')} multiline w={400}>
                                        <IconInfoCircle size={16} />
                                    </Tooltip>
                                </Flex>
                            }
                            {...form.getInputProps('syncPastEvents', { type: 'checkbox' })}
                        />
                        {form.values.syncPastEvents && (
                            <NumberInput
                                pl="xl"
                                maw={400}
                                label={t('dashboard.sync.past-events.lookback-label')}
                                min={1}
                                max={90}
                                {...form.getInputProps('pastEventsLookbackDays')}
                            />
                        )}
                        <Select
                            w="fit-content"
                            label={t('dashboard.sync.label-location')}
                            data={[
                                { value: 'true', label: t('common.yes') },
                                { value: 'false', label: t('common.no') },
                            ]}
                            allowDeselect={false}
                            {...form.getInputProps('keep_location')}
                        />
                        <Select
                            w="fit-content"
                            label={t('dashboard.sync.label-conference-link')}
                            data={[
                                { value: 'true', label: t('common.yes') },
                                { value: 'false', label: t('common.no') },
                            ]}
                            allowDeselect={false}
                            {...form.getInputProps('include_conference_link')}
                        />
                        <Select
                            w="fit-content"
                            label={t('dashboard.sync.label-free-busy')}
                            data={transparencyOptions}
                            allowDeselect={false}
                            {...form.getInputProps('transparency')}
                        />
                        <Select
                            w="fit-content"
                            label={
                                <Flex gap="xs" justify="flex-start" align="center" mb="0%">
                                    <Text size="sm" fw={500}>{t('dashboard.sync.label-maintain-event-id')}</Text>
                                    <Tooltip label={t('dashboard.sync.maintain-event-id-tooltip')} multiline w={500}>
                                        <IconInfoCircle size={16} />
                                    </Tooltip>
                                </Flex>
                            }
                            data={[
                                { value: 'true', label: t('common.yes') },
                                { value: 'false', label: t('common.no') },
                            ]}
                            allowDeselect={false}
                            {...form.getInputProps('maintainEventId')}
                        />
                    </Stack>
                </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="reminders">
                <Accordion.Header>{t('dashboard.sync.reminders.title')}</Accordion.Header>
                <Accordion.Body>
                    <RemindersInput form={form} />
                </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="filter">
                <Accordion.Header>{t('dashboard.sync.filter.title')}</Accordion.Header>
                <Accordion.Body>
                    <Stack gap="sm">
                        <Text c="dimmed" size="sm">{t('dashboard.sync.filter.description')}</Text>
                        <Flex gap="md" align="flex-start">
                            <Select
                                label={t('dashboard.sync.filter.field')}
                                data={[
                                    { label: t('dashboard.sync.filter.field-options.summary'), value: 'summary' },
                                    { label: t('dashboard.sync.filter.field-options.description'), value: 'description' },
                                ]}
                                style={{ flexBasis: '20%' }}
                                {...form.getInputProps('filterRuleField')}
                                clearable
                            />
                            <Select
                                label={t('dashboard.sync.filter.type')}
                                data={[
                                    { value: 'contains', label: t('dashboard.sync.filter.type-options.contains') },
                                    { value: 'regex', label: t('dashboard.sync.filter.type-options.regex') },
                                ]}
                                {...form.getInputProps('filterRuleType')}
                                style={{ flexBasis: '25%' }}
                                clearable
                            />
                            <TextInput
                                label={t('dashboard.sync.filter.rule')}
                                placeholder={t('dashboard.sync.filter.placeholder')}
                                {...form.getInputProps('filterRule')}
                                style={{ flexGrow: 1 }}
                            />
                        </Flex>
                        <Checkbox
                            label={t('dashboard.sync.filter.filter-in-label')}
                            description={t('dashboard.sync.filter.filter-in-description')}
                            {...form.getInputProps('filterIn', { type: 'checkbox' })}
                        />
                        <Button
                            variant="outline"
                            onClick={() => {
                                form.setFieldValue('filterRule', '');
                                form.setFieldValue('filterRuleField', null);
                                form.setFieldValue('filterRuleType', null);
                                form.setFieldValue('filterIn', false);
                            }}
                            flex="flex-start"
                            w="fit-content"
                            miw="80px"
                        >
                            {t('dashboard.sync.filter.clear')}
                        </Button>
                    </Stack>
                </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="buffer">
                <Accordion.Header>{t('dashboard.sync.buffer.title')}</Accordion.Header>
                <Accordion.Body>
                    <Stack gap="sm">
                        <Text c="dimmed" size="sm">{t('dashboard.sync.buffer.description')}</Text>
                        <Flex gap="md" align="flex-start">
                            <NumberInput
                                label={t('dashboard.sync.buffer.before')}
                                {...form.getInputProps('bufferBefore')}
                                min={0}
                            />
                            <NumberInput
                                label={t('dashboard.sync.buffer.after')}
                                {...form.getInputProps('bufferAfter')}
                                min={0}
                            />
                        </Flex>
                    </Stack>
                </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="status">
                <Accordion.Header>{t('dashboard.sync.status.title')}</Accordion.Header>
                <Accordion.Body>
                    <Stack gap="sm" mt="md">
                        <Text c="dimmed" size="sm">{t('dashboard.sync.status.description')}</Text>
                        {Object.keys(GoogleEventResponseStatus).map((status) => (
                            <Select
                                key={status}
                                label={
                                    <Text>
                                        {t('dashboard.sync.status.what-to-sync', {
                                            status: statusToHuman[GoogleEventResponseStatus[status as keyof typeof GoogleEventResponseStatus]],
                                        })}
                                    </Text>
                                }
                                data={statusOptions}
                                {...form.getInputProps(`statusTransform.${status}`)}
                            />
                        ))}
                        <Checkbox
                            label={t('dashboard.sync.label-declined')}
                            {...form.getInputProps('statusTransform.deleted_declined', { type: 'checkbox' })}
                        />
                    </Stack>
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );
};
